/**
 * Utilitaires pour transformer le texte des posts
 */

/**
 * Transforme les mentions du format @[nom](urn:...) en nom simple pour l'affichage en texte brut
 */
export function transformMentionsToText(text: string): string {
  return text.replace(/@\[(.+?)\]\((.+?)\)/g, (match, name: string) => {
    return name;
  });
}

/**
 * Transforme les mentions du format @[nom](urn:...) en liens HTML pour l'affichage dans ActivityPreview
 */
export function transformMentionsToHtml(text: string): string {
  return text.replace(/@\[(.+?)\]\((.+?)\)/g, (match, name) => {
    return `<span class="white-space-pre"> </span>
      <a class="mention-link" target="_self" href="#"">
        <!---->${name}<!---->
      </a>`;
  });
}

/**
 * Transforme les hashtags en liens HTML
 *
 * `\p{L}` (toutes les lettres, accentuées ou non, quel que soit l'alphabet) et `\p{N}` (les
 * chiffres) au lieu de `\w`, qui vaut `[A-Za-z0-9_]` et s'arrête donc au premier caractère
 * accentué : `#LinkedinActualités` ne produisait que le lien `keywords=LinkedinActualit`.
 * Le drapeau `u` est obligatoire pour que `\p{...}` soit interprété.
 *
 * Cette définition reste volontairement plus stricte que celle de l'éditeur
 * (`/(?:^|\s)#[^\s.,;:!?]+/gi` dans components/QuillEditor/modules/hashtag.ts), pour deux
 * raisons :
 * - l'éditeur accepte dans un hashtag tout ce qui n'est ni une espace ni `.,;:!?` (donc `-`,
 *   `/`, `'`…) alors que LinkedIn, lui, coupe le hashtag au premier caractère non
 *   alphanumérique — l'aperçu doit reproduire le rendu de LinkedIn, pas celui de l'éditeur ;
 * - l'éditeur exige une espace avant le `#` pour ne pas surligner en cours de frappe, alors
 *   que LinkedIn transforme aussi `(#hashtag)`.
 */
export function transformHashtagsToHtml(text: string): string {
  return text.replace(/#([\p{L}\p{N}_]+)/gu, (match, hashtag) => {
    const href = `https://www.linkedin.com/feed/hashtag/?keywords=${encodeURIComponent(hashtag as string)}`;
    return `<a class="hashtag-link " href="${href}">
      <span><span aria-hidden="true">#</span>${hashtag}</span>
    </a>`;
  });
}

/**
 * Transforme les liens en balises <a>
 */
export function transformLinksToHtml(text: string): string {
  return text.replace(/(https?:\/\/[^\s]+)/g, (url) => {
    const displayedUrl = url.length > 26 ? `https://lnkd.in/XXXXXXXX` : url;
    return `<a class="link " target="_self" href="${url}" data-test-app-aware-link="">
      <!---->${displayedUrl}<!---->
    </a>`;
  });
}

/**
 * Transforme le texte complet en HTML pour ActivityPreview
 */
export function transformTextToHtml(inputText: string): string {
  // Divise le texte par les sauts de ligne
  const lines = inputText.replaceAll('\\', '').split('\n');

  // Construit les balises HTML pour chaque ligne
  const transformedHtml = lines
    .map((line) => {
      // Si la ligne est vide, ajoute uniquement un <br>
      if (line.trim() === '') {
        return '<span><br></span>';
      }

      // Transforme les hashtags, mentions et liens
      const transformedLine = transformHashtagsToHtml(transformMentionsToHtml(transformLinksToHtml(line.trim())));
      return `<!---->${transformedLine}<!----><span><br></span>`;
    })
    .join('');

  return transformedHtml;
}
